import type React from "react";
import {useMemo, useState} from "react";
import {Box, Button, Skeleton, Stack, Typography} from "@mui/material";
import {Add} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router";
import ArrowIcon from '~/icons/svg/ArrowIcon.svg?react';
import ValueChainIcon from "~/icons/svg/ValueChainIcon.svg?react";
import VerifiedIcon from '~/icons/svg/VerifiedIcon.svg?react';
import InfoIcon from '~/icons/svg/InfoIcon.svg?react';
import ScheduleIcon from '~/icons/svg/ScheduleIcon.svg?react';
import {StatChip} from "~/components/StatChip/StatChip";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import {COLORS} from "~/style/colors-ts";
import type {Supplier360RouteMatch} from "~/routing/catRoutes";
import {useSupplierEntityQuery} from "~/pages/supplier-360/hooks/useSupplierEntityQuery";
import {
    useChainEntitlementQuery,
    useChainPendingCreditRequestsQuery,
    useChainUnlocksQuery,
} from "~/pages/supplier-360/tabs/SupplierValueChain/hooks/useChainCreditQueries";
import {creditsUsed, formatCreditDate} from "~/pages/supplier-360/tabs/SupplierValueChain/chainCredits";
import {ChainPendingRequest} from "~/pages/supplier-360/tabs/SupplierValueChain/components/ChainPendingRequest";
import {ChainRequestCreditsDialog} from "~/pages/supplier-360/tabs/SupplierValueChain/components/ChainRequestCreditsDialog";
import {getMappedCompaniesColumns} from "./mappedCompaniesColumns";

/** A standalone page: its own padding and scroll, like `SupplierDetailLayout`'s container. */
const PAGE_SX = {p: 2, height: 'calc(100vh - 64px)', overflow: 'auto'};

/**
 * "Value chain credits" (`ChainCreditsPage` in the design), at `:supplierId/value-chain/credits` —
 * replaces the Manage credits dialog. Balance KPIs, any pending request, and every company mapped
 * under the databag's entitlement (§2.14–§2.16).
 */
export const ValueChainCreditsPage: React.FC = () => {
    const {supplierId} = useParams<Supplier360RouteMatch>();
    const navigate = useNavigate();
    const [requesting, setRequesting] = useState(false);

    const {data: entity, isLoading: isEntityLoading, isError: isEntityError} = useSupplierEntityQuery(supplierId);
    const entitlementQuery = useChainEntitlementQuery(entity?.databag_id);
    const entitlement = entitlementQuery.data ?? undefined;
    const unlocksQuery = useChainUnlocksQuery(entitlement?.id);
    const pendingQuery = useChainPendingCreditRequestsQuery(entitlement?.id);
    const pendingRequests = pendingQuery.data ?? [];

    const columns = useMemo(() => getMappedCompaniesColumns(), []);
    const goBack = () => navigate('..', {relative: 'path'});

    const backButton = (
        <Button
            size="small"
            onClick={goBack}
            startIcon={<ArrowIcon width={16} height={16}/>}
            sx={{alignSelf: 'flex-start', textTransform: 'none'}}
        >
            {entity?.canonical_name ?? 'Value chain'}
        </Button>
    );

    if (isEntityLoading || entitlementQuery.isLoading) {
        return <Box sx={PAGE_SX}><Skeleton variant="rectangular" height={480} sx={{borderRadius: 1}}/></Box>;
    }

    if (isEntityError || entitlementQuery.isError || !entitlement) {
        return (
            <Stack gap={1} sx={{...PAGE_SX, alignItems: 'flex-start'}}>
                {backButton}
                <Typography color="text.secondary">
                    {entitlementQuery.isError || isEntityError
                        ? 'Value chain credits could not be loaded.'
                        : "Value chain isn't enabled for this databag. Contact Mithra to add credits."}
                </Typography>
            </Stack>
        );
    }

    const remaining = entitlement.credit_balance;
    const hasPending = pendingRequests.length > 0;

    return (
        <Stack gap={2} sx={PAGE_SX}>
            {backButton}

            <Stack direction="row" alignItems="center" gap={2}>
                <Typography component="h1" variant="h5" fontWeight={700}>Value chain credits</Typography>
                {!hasPending && (
                    <Button
                        variant="contained"
                        startIcon={<Add/>}
                        onClick={() => setRequesting(true)}
                        sx={{textTransform: 'none', ml: 'auto'}}
                    >
                        Request credits
                    </Button>
                )}
            </Stack>

            <Stack direction="row" flexWrap="wrap" useFlexGap gap="10px">
                <StatChip
                    icon={{element: <ValueChainIcon/>, color: COLORS.secondary.main}}
                    title="Entitlement"
                    value={entitlement.credits_granted}
                    description="companies"
                />
                <StatChip
                    icon={{element: <VerifiedIcon/>, color: COLORS.info.main}}
                    title="Mapped"
                    value={creditsUsed(entitlement)}
                />
                <StatChip
                    icon={{element: <InfoIcon/>, color: remaining === 0 ? COLORS.warning.main : COLORS.success.main}}
                    title="Remaining"
                    value={remaining}
                />
                <StatChip
                    icon={{element: <ScheduleIcon/>, color: COLORS.secondary.main}}
                    title="Valid until"
                    value={formatCreditDate(entitlement.expires_at)}
                />
            </Stack>

            {pendingRequests.map(request => <ChainPendingRequest key={request.id} request={request}/>)}

            {unlocksQuery.isError
                ? <Typography color="text.secondary">Could not load the mapped companies.</Typography>
                : (
                    <Box sx={{height: 480}}>
                        <CustomDataGrid
                                layoutKey="supplier-360-value-chain-credits"
                                rows={unlocksQuery.data ?? []}
                                columns={columns}
                                loading={unlocksQuery.isLoading}
                                disableRowSelectionOnClick
                                showToolbar
                                toolbarProps={{title: `${unlocksQuery.data?.length ?? 0} companies mapped`, showFilterPanel: false}}
                                localeText={{noRowsLabel: 'No company chains mapped yet.'}}
                        />
                    </Box>
                )}

            {requesting && (
                <ChainRequestCreditsDialog entitlement={entitlement} onClose={() => setRequesting(false)}/>
            )}
        </Stack>
    );
};
